import { usePageCrumb } from '@ps/ui-kit/components/app-shell';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { ApiError } from '@ps/api-client/http';
import { staffApi, type CustomerDetail } from '@ps/api-client/staff';
import { Alert } from '@ps/ui-kit/components/alert';
import { Button } from '@ps/ui-kit/components/button';
import { Card, CardTitle, Details } from '@ps/ui-kit/components/card';
import { Dialog } from '@ps/ui-kit/components/dialog';
import { Field, Select } from '@ps/ui-kit/components/field';
import { Input } from '@ps/ui-kit/components/input';
import { Pill } from '@ps/ui-kit/components/pill';
import { SearchBar } from '@ps/ui-kit/components/search-bar';
import { Empty, ErrorState, Loading } from '@ps/ui-kit/components/states';
import { amount, amountChars, date, dateTime, titleCase, toMinor } from '@ps/ui-kit/lib/format';
import { useBlurValidation } from '@ps/ui-kit/lib/use-blur-validation';
import { minChars } from '@ps/ui-kit/lib/validate';
import { LinkRow, Table } from '@ps/ui-kit/components/table';
import { BAND_TONE } from '@ps/ui-internal/onboarding/labels';
import { EmailHistory } from '@ps/ui-internal/email-history';

// A-10 Customer master. Verified fields (address, ID document number and place of issue, DOB, place of birth) feed the
// originator information on every wire (WTR-03), so only Operations Manager / Compliance Officer may amend them, each
// with an evidence reference; every change is kept and audited (CUSTOMER_AMEND).

const api = staffApi('admin');
const SENDS = { ADDRESS: 'Verified address', ID_DOC_NUMBER: 'Verified ID document number', DOB_AND_PLACE_OF_BIRTH: 'Verified date and place of birth' } as const;
const FIELDS = [
  { value: 'ADDRESS', label: 'Address' }, { value: 'ID_DOC_NUMBER', label: 'ID document number' }, { value: 'ID_DOC_PLACE', label: 'ID document place of issue' },
  { value: 'DOB', label: 'Date of birth (YYYY-MM-DD)' }, { value: 'PLACE_OF_BIRTH', label: 'Place of birth' },
];

export function CustomerList({ onOpen }: { onOpen: (id: string) => void }) {
  const [search, setSearch] = React.useState('');
  const [term, setTerm] = React.useState('');
  React.useEffect(() => { const t = setTimeout(() => setTerm(search.trim()), 300); return () => clearTimeout(t); }, [search]);
  const q = useQuery({ queryKey: ['customers', term], queryFn: () => api.customers({ search: term || undefined }) });
  return (
    <div>
      <h1 className="font-display text-xl font-bold text-ink">Customer master</h1>
      <SearchBar label="Search customers" placeholder="Search CST reference or name" value={search} onChange={setSearch} className="mt-4" />
      <Card className="mt-4 overflow-x-auto p-0">
        {q.isPending ? <Loading /> : q.error ? <div className="p-4"><ErrorState message="We couldn't load customers." onRetry={() => q.refetch()} /></div> : !q.data?.items.length ? <div className="p-4"><Empty title="No customers yet">Customers appear here when an application is approved.</Empty></div> : (
          <Table bare>
            <thead><tr><th>Reference</th><th>Customer</th><th>Type</th><th>Status</th><th>Risk</th><th>Next review</th></tr></thead>
            <tbody>
              {q.data.items.map(c => (
                <LinkRow key={c.id} onOpen={() => onOpen(c.id)}>
                  <td className="font-mono text-xs"><button className="hover:underline">{c.reference}</button></td>
                  <td className="font-semibold text-ink">{c.legalName}</td><td>{titleCase(c.type)}</td>
                  <td><Pill tone={c.status === 'ACTIVE' ? 'ok' : 'warn'} upper>{c.status}</Pill></td>
                  <td><Pill tone={BAND_TONE[c.riskBand] ?? 'neu'} upper>{c.riskBand}</Pill></td>
                  <td>{date(c.nextReviewDue)}</td>
                </LinkRow>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}

export function CustomerView({ id, canAmend, canRecord = false }: { id: string; canAmend: boolean; canRecord?: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['customer', id], queryFn: () => api.customer(id) });
  usePageCrumb(q.data?.customer.reference);
  const [amending, setAmending] = React.useState(false);
  const wallets = useQuery({ queryKey: ['customer', id, 'wallets'], queryFn: () => api.customerWallets(id) });
  const bens = useQuery({ queryKey: ['customer', id, 'beneficiaries'], queryFn: () => api.customerBeneficiaries(id) });
  const receipts = useQuery({ queryKey: ['customer', id, 'collections'], queryFn: () => api.customerCollections(id) });
  const [recording, setRecording] = React.useState(false);
  const [verifying, setVerifying] = React.useState<CustomerDetail['members'][number] | null>(null);
  if (q.isPending) return <Loading />;
  if (q.error) return <ErrorState message="We couldn't load this customer." onRetry={() => q.refetch()} />;
  const { customer: c, originator: o, alerts, members, changes } = q.data!;
  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center gap-2">
        <h1 className="mr-2 font-display text-xl font-bold text-ink">{c.legalName}</h1>
        <span className="font-mono text-sm text-ink-3">{c.reference}</span>
        <Pill tone={c.status === 'ACTIVE' ? 'ok' : 'warn'} upper>{c.status}</Pill>
        <Pill tone={BAND_TONE[c.riskBand] ?? 'neu'} upper>{c.riskBand}</Pill>
      </header>
      {!o.completeForWire && (
        <Alert tone="warn" title="Payments of HK$8,000 or more will be refused">
          No verified address, ID document number, or date and place of birth is on file, so the originator information required for these
          payments is incomplete (WTR-01). Add one of them with its evidence.
        </Alert>
      )}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardTitle>Relationship</CardTitle>
          <Details rows={[['Type', c.type], ['Relationship started', date(c.relationshipStarted)], ['Next review', date(c.nextReviewDue)],
            ['Payment approvals', c.approvalRequired ? 'Required' : 'Not required'], ['Alerts', `${alerts.open} open · ${alerts.total} in total`]]} />
        </Card>
        <Card>
          <CardTitle aside={canAmend ? <Button size="sm" variant="secondary" onClick={() => setAmending(true)}>Amend</Button> : undefined}>Verified details</CardTitle>
          <Details rows={[['Address', c.verified.address], ['ID document number', c.verified.idDocNumber], ['ID place of issue', c.verified.idDocPlaceOfIssue], ['Date of birth', c.verified.dob ? date(c.verified.dob) : null], ['Place of birth', c.verified.placeOfBirth]]} />
          <p className="mt-3 text-xs text-ink-3">Used as originator information on every payment. Customers cannot change these themselves.</p>
        </Card>
      </div>
      <Card>
        <CardTitle>Originator information sent with payments</CardTitle>
        <Details rows={[
          ['Name', o.name], ['Account reference', <span key="a" className="font-mono">{o.accountRef}</span>],
          ['From HK$8,000 also', o.sends ? SENDS[o.sends] : <span key="m" className="font-semibold text-bad">Missing: no verified identifier on file</span>],
        ]} />
      </Card>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardTitle>Wallets</CardTitle>
          {wallets.isPending ? <Loading /> : wallets.error ? <ErrorState message="We couldn't load the wallets." /> : !wallets.data.length ? <p className="text-sm text-ink-3">No wallets.</p> : (
            <ul className="divide-y divide-line text-sm">{wallets.data.map(w => (
              <li key={w.currency} className="flex flex-wrap justify-between gap-3 py-2"><span className="font-semibold text-ink">{w.currency}</span>
                <span className="tabular">{amount(w.availableMinor, w.currency)} available{w.onHoldMinor !== '0' && <> · {amount(w.onHoldMinor, w.currency)} on hold</>}</span></li>
            ))}</ul>
          )}
        </Card>
        <Card>
          <CardTitle>Beneficiaries</CardTitle>
          {bens.isPending ? <Loading /> : bens.error ? <ErrorState message="We couldn't load the beneficiaries." /> : !bens.data.items.length ? <p className="text-sm text-ink-3">No beneficiaries.</p> : (
            <ul className="divide-y divide-line text-sm">{bens.data.items.map(b => (
              <li key={b.id} className="flex flex-wrap items-center gap-3 py-2">
                <span className="font-semibold text-ink">{b.name}</span><span className="text-ink-3">{b.bankName} · {b.accountMasked} · {b.currency} · {b.country}</span>
                <Pill tone={b.status === 'ACTIVE' ? 'ok' : 'neu'} upper className="ml-auto">{b.status}</Pill>
              </li>
            ))}</ul>
          )}
        </Card>
      </div>
      <Card>
        <CardTitle aside={canRecord && c.status === 'ACTIVE' ? <Button size="sm" variant="secondary" onClick={() => setRecording(true)}>Record receipt</Button> : undefined}>Money received</CardTitle>
        {receipts.isPending ? <Loading /> : receipts.error ? <ErrorState message="We couldn't load the receipts." /> : !receipts.data.items.length ? <p className="text-sm text-ink-3">No receipts.</p> : (
          <ul className="divide-y divide-line text-sm">{receipts.data.items.map(r => (
            <li key={r.id} className="flex flex-wrap items-center gap-3 py-2">
              <span className="font-mono text-xs">{r.reference}</span><span className="font-semibold text-ink">{r.payerName}</span>
              <span className="tabular">{amount(r.amountMinor, r.currency)}</span><span className="text-ink-3">name score {r.nameScore}</span>
              <Pill tone={r.status === 'CREDITED' ? 'ok' : r.status === 'HELD' ? 'warn' : 'neu'} upper className="ml-auto">{r.status}{r.holdReason === 'RESTING_LIMIT' ? ' · over limit' : ''}</Pill>
            </li>
          ))}</ul>
        )}
      </Card>
      <Card>
        <CardTitle>Team members</CardTitle>
        <ul className="divide-y divide-line text-sm">{members.filter(m => m.status !== 'REMOVED').map(m => (
          <li key={m.userId} className="flex flex-wrap items-center gap-3 py-2">
            <span className="font-semibold text-ink">{m.displayName ?? m.userId}</span>{m.email && <span className="text-ink-3">{m.email}</span>}
            <Pill tone="neu" upper>{m.role}</Pill>
            <Pill tone={m.status === 'ACTIVE' ? 'ok' : 'warn'} upper>{m.status === 'INVITED' ? 'Invited · identity check pending' : m.status}</Pill>
            {m.status === 'INVITED' && canAmend && <Button size="sm" variant="secondary" className="ml-auto" onClick={() => setVerifying(m)}>Record identity check</Button>}
          </li>
        ))}</ul>
      </Card>
      <EmailHistory queryKey={['customer', id]} load={() => api.customerEmails(id)} />
      <Card>
        <CardTitle>Change log</CardTitle>
        {!changes.length ? <p className="text-sm text-ink-3">No amendments.</p> : (
          <ul className="divide-y divide-line text-sm">{changes.map((ch, i) => (
            <li key={i} className="py-2">
              <div className="flex flex-wrap gap-3"><span className="font-semibold text-ink">{ch.field.replaceAll('_', ' ').toLowerCase()}</span>
                <span className="text-ink-2">by {ch.changedBy}</span><span className="text-ink-2">evidence: {ch.evidenceRef}</span><span className="ml-auto text-ink-3">{dateTime(ch.changedAt)}</span></div>
              <p className="mt-1 text-ink-2"><span className="text-ink-3">{ch.oldValue ?? '(none)'}</span> → <span className="text-ink">{ch.newValue}</span></p>
            </li>
          ))}</ul>
        )}
      </Card>
      <ReceiptDialog open={recording} onClose={() => setRecording(false)} reference={c.reference} customerId={id} />
      <AmendDialog open={amending} onClose={() => setAmending(false)} customer={q.data!} onDone={d => { qc.setQueryData(['customer', id], d); setAmending(false); }} />
      {verifying && <VerifyMemberDialog customerId={id} member={verifying} onClose={() => setVerifying(null)}
        onDone={() => { setVerifying(null); void qc.invalidateQueries({ queryKey: ['customer', id] }); void qc.invalidateQueries({ queryKey: ['work'] }); }} />}
    </div>
  );
}

function AmendDialog({ open, onClose, customer, onDone }: { open: boolean; onClose: () => void; customer: CustomerDetail; onDone: (d: CustomerDetail) => void }) {
  const [field, setField] = React.useState('ADDRESS');
  const [value, setValue] = React.useState('');
  const [evidence, setEvidence] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const v = useBlurValidation({
    value: () => (!value.trim() ? 'Enter the new value' : field === 'DOB' && !/^\d{4}-\d{2}-\d{2}$/.test(value.trim()) ? 'Enter the date as YYYY-MM-DD' : null),
    evidence: () => minChars(evidence, 5),
  });
  async function save() {
    if (!v.touchAll()) return;
    setBusy(true); setError(null);
    try { onDone(await api.amend(customer.customer.id, { field, value, evidenceRef: evidence, version: customer.customer.version })); setValue(''); setEvidence(''); v.reset(); }
    catch (e) { setError(e instanceof ApiError ? e.body.message : 'Something went wrong.'); }
    finally { setBusy(false); }
  }
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()} title="Amend a verified field" description="The change and its evidence are recorded in the change log and the audit trail."
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save amendment'}</Button></>}>
      <div className="space-y-3">
        <Field id="am-field" label="Field"><Select id="am-field" value={field} onChange={e => setField(e.target.value)}>{FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}</Select></Field>
        <Field id="am-value" label="New value" error={v.errorOf('value')}><Input id="am-value" value={value} onChange={e => setValue(e.target.value)} {...v.field('value', 'am-value')} /></Field>
        <Field id="am-evidence" label="Evidence reference" hint="e.g. utility bill dated 2026-09, document reference" error={v.errorOf('evidence')}><Input id="am-evidence" value={evidence} onChange={e => setEvidence(e.target.value)} {...v.field('evidence', 'am-evidence')} /></Field>
        {error && <Alert tone="bad">{error}</Alert>}
      </div>
    </Dialog>
  );
}

/** Operations: record a receipt seen on the client account statement (MVP "simulate receipt"; a bank feed later). */
function ReceiptDialog({ open, onClose, reference, customerId }: { open: boolean; onClose: () => void; reference: string; customerId: string }) {
  const qc = useQueryClient();
  const [ccy, setCcy] = React.useState('HKD');
  const [amt, setAmt] = React.useState('');
  const [payer, setPayer] = React.useState('');
  const [account, setAccount] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<string | null>(null);
  const minor = toMinor(amt, ccy);
  const v = useBlurValidation({
    amount: () => (!minor || minor === '0' ? 'Enter an amount above zero, e.g. 1,000.00' : null),
    payer: () => (!payer.trim() ? 'Enter the payer name exactly as on the bank statement' : null),
  });
  async function save() {
    if (!v.touchAll()) return;
    setBusy(true); setErr(null);
    try {
      const r = await api.recordReceipt({ customerReference: reference, currency: ccy, amountMinor: minor!, payerName: payer.trim(), payerAccount: account.trim() || null });
      setResult(`${r.reference}: ${r.status.toLowerCase()}${r.status === 'HELD' ? ' in suspense; an alert is in the Console C1 queue for compliance review' : r.holdReason === 'RESTING_LIMIT' ? ' to the sender (over the resting balance maximum)' : ''}.`);
      setAmt(''); setPayer(''); setAccount(''); v.reset();   // a cleared form is not an invalid one
      await qc.invalidateQueries({ queryKey: ['customer', customerId] });
    } catch (e) { setErr(e instanceof ApiError ? e.body.message : 'Something went wrong. Please try again.'); }
    finally { setBusy(false); }
  }
  return (
    <Dialog open={open} onOpenChange={o => { if (!o) { setResult(null); setErr(null); onClose(); } }} title="Record a receipt"
      description={`Money seen on the client account statement with the reference ${reference}. The payer name is checked against the account holder.`}
      footer={<><Button variant="ghost" onClick={onClose}>Close</Button><Button onClick={save} disabled={busy}>{busy ? 'Recording…' : 'Record receipt'}</Button></>}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field id="rc-ccy" label="Currency"><Select id="rc-ccy" value={ccy} onChange={e => setCcy(e.target.value)}>{['HKD', 'USD', 'SGD', 'EUR', 'GBP', 'CNY'].map(c => <option key={c}>{c}</option>)}</Select></Field>
        <Field id="rc-amt" label="Amount" error={v.errorOf('amount')}><Input id="rc-amt" inputMode="decimal" value={amt} onChange={e => setAmt(amountChars(e.target.value))} {...v.field('amount', 'rc-amt')} /></Field>
        <Field id="rc-payer" label="Payer name (as on the statement)" className="sm:col-span-2" error={v.errorOf('payer')}><Input id="rc-payer" value={payer} maxLength={140} onChange={e => setPayer(e.target.value)} {...v.field('payer', 'rc-payer')} /></Field>
        <Field id="rc-acct" label="Payer account (optional)" className="sm:col-span-2"><Input id="rc-acct" value={account} maxLength={40} onChange={e => setAccount(e.target.value)} /></Field>
      </div>
      {err && <Alert tone="bad" className="mt-3">{err}</Alert>}
      {result && <Alert tone="ok" className="mt-3">{result}</Alert>}
    </Dialog>
  );
}

/** P-C12: an invited member acts for the company only after their identity check is recorded here, with its reference. */
function VerifyMemberDialog({ customerId, member: m, onClose, onDone }: { customerId: string; member: CustomerDetail['members'][number]; onClose: () => void; onDone: () => void }) {
  const [evidenceRef, setRef] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const v = useBlurValidation({ ref: () => minChars(evidenceRef, 5) });
  const submit = async () => {
    if (!v.touchAll()) return;
    setBusy(true); setError(null);
    try { await api.verifyMember(customerId, m.userId, { evidenceRef: evidenceRef.trim(), version: m.version }); onDone(); }
    catch (e) { setError(e instanceof ApiError ? e.body.message : 'Something went wrong. Please try again.'); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }} title="Record identity check" description={`${m.displayName ?? m.userId} · ${m.role}`}
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button disabled={busy} onClick={() => void submit()}>Activate member</Button></>}>
      <Field id="vm-ref" label="Identity check reference" hint="The KYC check or document reference that verifies this person (at least 5 characters)." error={v.errorOf('ref')}>
        <Input id="vm-ref" value={evidenceRef} onChange={e => setRef(e.target.value)} {...v.field('ref', 'vm-ref')} />
      </Field>
      {error && <Alert tone="bad" className="mt-3">{error}</Alert>}
    </Dialog>
  );
}
